/* ═══════════════════════════════════════════════════════════
   js/catalog-facets.js — общий механизм подсчёта каталога ЖК
   (дизайн каталога, шаг А2, 24.09.2026; спецификация раздел А2.7).

   Зачем (человеческим языком). Окно «Метро и районы», а следом и левая колонка
   фильтров (шаг В) должны называть ОДНИ И ТЕ ЖЕ числа: у строки «Павелецкая 7»,
   на кнопке «Показать 7 ЖК» и в выдаче после нажатия. Три копии правила разошлись
   бы при первой правке, поэтому правило одно и живёт здесь.

   Что внутри — только чистые функции, без DOM (как build-status.js): их гоняет
   node в tests/JS/catalog-facets.test.js, а страница зовёт те же функции.
     • где искать: станции (с правилом «время до метро»), линии, округа, районы,
       города и посёлки — всё через ИЛИ (решение 5 главного чата: «у Павелецкой
       ИЛИ в ЦАО»), с остальными фильтрами страницы — через И;
     • числа у строк: все фильтры страницы, КРОМЕ самого «где искать» — поэтому
       выбранные станции чисел не меняют, и числа не «схлопываются» после нажатия;
     • черновик выбора: «вся линия», «весь округ», снятие одной станции из целой
       линии, свёртка полностью выбранной линии в одну метку;
     • поиск по станциям, линиям, округам, районам и местам (начало любого слова,
       «ё» = «е», без учёта регистра).

   ⛔ ВСЁ СЧИТАЕТСЯ В ЖК, А НЕ В КВАРТИРАХ (слово владельца 24.09): «набирает метро
      Павелецкая — на кнопке показывать 4 ЖК на этой станции». Квартиры в браузер
      не отдаются вовсе (решение 6 главного чата); признаки квартиры — шаг В, сервер.

   ⛔ ПРАВИЛО СТАНЦИИ — ЗЕРКАЛО СЕРВЕРА (CatalogGeoWhere::metroStationsWhere):
      «пешком» = walk === true; потолок минут = time задано И time <= потолка
      (NULL в SQL `bm.time <= ?` не проходит — здесь тоже); без потолка время
      не спрашивается. Совпадение сторожит tools/catalog-facets-parity.mjs
      на 10 сочетаниях живого стенда.
   ═══════════════════════════════════════════════════════════ */
(() => {
  'use strict';

  var KINDS = ['ln', 'st', 'ok', 'ds', 'pl'];
  var LIST_OF = { ln: 'lines', st: 'stations', ok: 'okrugs', ds: 'districts', pl: 'places' };

  // ══════════════ ТЕКСТ И ПОИСК ══════════════

  /**
   * Ключ сравнения той же длины, что исходная строка: нижний регистр, «ё» → «е»,
   * дефис и скобки — в пробел. Длина сохраняется намеренно: по найденной позиции
   * подсвечивается кусок ИСХОДНОЙ подписи.
   */
  function norm(s) {
    return String(s == null ? '' : s).toLowerCase()
      .replace(/ё/g, 'е')
      .replace(/[-‐–—().,«»"']/g, ' ');
  }

  /** Запрос: тот же ключ плюс сжатые пробелы по краям и внутри. */
  function normQuery(q) {
    return norm(q).replace(/\s+/g, ' ').trim();
  }

  /** Позиция совпадения с НАЧАЛА любого слова подписи или -1 («кур» → «Курская»). */
  function matchAt(name, q) {
    if (!q) return -1;
    var n = norm(name);
    if (n.indexOf(q) === 0) return 0;
    var i = n.indexOf(' ' + q);
    return i === -1 ? -1 : i + 1;
  }

  /**
   * Поиск по готовому списку записей.
   * entry = { k: 'st'|'ln'|'ok'|'ds'|'pl', key, name, alt?: [строки], exact?: [строки] }
   *   alt   — другие подписи, где тоже ищем с начала слова («ЦАО» у округа);
   *   exact — подписи, совпадающие только целиком (номер линии «2» не ловит «12»).
   * Ответ — [{ e, at, len, field }]: at/len — что подсветить в поле field ('name' | номер alt).
   */
  function search(query, entries) {
    var q = normQuery(query);
    if (!q) return [];
    var out = [];
    (entries || []).forEach(function(e) {
      var at = matchAt(e.name, q);
      var field = 'name';
      if (at === -1 && e.alt) {
        for (var i = 0; i < e.alt.length; i++) {
          at = matchAt(e.alt[i], q);
          if (at !== -1) { field = i; break; }
        }
      }
      if (at === -1 && e.exact) {
        for (var j = 0; j < e.exact.length; j++) {
          if (normQuery(e.exact[j]) === q) { at = 0; field = 'exact'; break; }
        }
      }
      if (at !== -1) out.push({ e: e, at: at, len: q.length, field: field });
    });
    // Совпадение с начала всей подписи — выше совпадения со второго слова.
    out.sort(function(a, b) {
      return (a.at === 0 ? 0 : 1) - (b.at === 0 ? 0 : 1)
        || String(a.e.name).localeCompare(String(b.e.name), 'ru');
    });
    return out;
  }

  // ══════════════ СПРАВОЧНИК ══════════════

  /**
   * Справочник сети (window.BroniGeo[сеть] из public/js/data/geo-*.js) → удобные
   * карты. null на входе — null на выходе: без справочника работают данные ЖК.
   */
  function prepareGeo(raw) {
    if (!raw || !Array.isArray(raw.lines)) return null;
    var lineById = {};
    raw.lines.forEach(function(ln) { lineById[ln.id] = ln; });
    var okrugById = {};
    (raw.ok || []).forEach(function(o) { okrugById[o.s] = o; });
    var districtById = {};
    var okrugDistricts = {};
    (raw.ds || []).forEach(function(d) {
      districtById[d.s] = d;
      if (d.o) (okrugDistricts[d.o] = okrugDistricts[d.o] || []).push(d.s);
    });
    return {
      raw: raw,
      lines: raw.lines,
      st: raw.st || {},
      ok: raw.ok || [],
      ds: raw.ds || [],
      lineById: lineById,
      okrugById: okrugById,
      districtById: districtById,
      okrugDistricts: okrugDistricts
    };
  }

  /** Линии станции по справочнику ([] — станции в справочнике нет). */
  function stationLines(geo, slug) {
    var s = geo && geo.st[slug];
    return s && Array.isArray(s.l) ? s.l : [];
  }

  /** Станции линии по порядку схемы. */
  function lineStations(geo, id) {
    var ln = geo && geo.lineById[id];
    return ln && Array.isArray(ln.st) ? ln.st : [];
  }

  // ══════════════ ИНДЕКС ПО ДАННЫМ ЖК ══════════════

  /**
   * Индекс выдачи — однажды на загрузку города: что вообще есть в данных.
   * Нужен для того, чего нет в справочнике: подписи мест и районов городов без
   * справочника, станции «из данных» (запасной список, если справочник не пришёл),
   * проверка слагов из адреса.
   */
  function buildIndex(items) {
    var idx = { st: {}, ok: {}, ds: {}, pl: {}, names: { st: {}, ds: {}, pl: {} }, stLine: {}, filled: { ds: 0, pl: 0 } };
    (items || []).forEach(function(it) {
      var id = it && it.id;
      if (id == null) return;
      (Array.isArray(it.metro_full) ? it.metro_full : []).forEach(function(s) {
        if (!s || !s.slug) return;
        var slug = String(s.slug);
        (idx.st[slug] = idx.st[slug] || []).push(id);
        if (!idx.names.st[slug] && s.name) idx.names.st[slug] = String(s.name);
        if (!idx.stLine[slug]) idx.stLine[slug] = { line: String(s.line || ''), color: String(s.color || '') };
      });
      if (it.okrug) (idx.ok[it.okrug] = idx.ok[it.okrug] || []).push(id);
      if (it.rayon) {
        (idx.ds[it.rayon] = idx.ds[it.rayon] || []).push(id);
        idx.filled.ds++;
        if (it.rayon_name && !idx.names.ds[it.rayon]) idx.names.ds[it.rayon] = String(it.rayon_name);
      }
      if (it.place) {
        (idx.pl[it.place] = idx.pl[it.place] || []).push(id);
        idx.filled.pl++;
        if (it.place_name && !idx.names.pl[it.place]) idx.names.pl[it.place] = String(it.place_name);
      }
    });
    return idx;
  }

  // ══════════════ ЧЕРНОВИК ВЫБОРА ══════════════

  function emptyDraft() {
    return { stations: [], lines: [], okrugs: [], districts: [], places: [], min: 0, mode: 'any' };
  }

  function arr(v) { return Array.isArray(v) ? v.slice() : []; }

  /** Глубокая копия черновика; недостающие поля — пустые (старый вид { stations, min, mode } тоже годится). */
  function cloneDraft(d) {
    d = d || {};
    return {
      stations: arr(d.stations), lines: arr(d.lines), okrugs: arr(d.okrugs),
      districts: arr(d.districts), places: arr(d.places),
      min: +d.min > 0 ? +d.min : 0,
      mode: d.mode === 'walk' ? 'walk' : 'any'
    };
  }

  function has(list, v) { return list.indexOf(v) !== -1; }
  function add(list, v) { if (!has(list, v)) list.push(v); }
  function drop(list, v) { var i = list.indexOf(v); if (i !== -1) list.splice(i, 1); }

  /** Сколько всего выбрано (метка «Метро и районы · 3»): линия и округ считаются одним. */
  function draftSize(d) {
    return d.stations.length + d.lines.length + d.okrugs.length + d.districts.length + d.places.length;
  }

  /** Станция выбрана — сама или своей целой линией. */
  function stationOn(d, slug, geo) {
    if (has(d.stations, slug)) return true;
    var ls = stationLines(geo, slug);
    for (var i = 0; i < ls.length; i++) { if (has(d.lines, ls[i])) return true; }
    return false;
  }

  /** Район выбран — сам или своим целым округом. */
  function districtOn(d, slug, geo) {
    if (has(d.districts, slug)) return true;
    var ds = geo && geo.districtById[slug];
    return !!(ds && ds.o && has(d.okrugs, ds.o));
  }

  function isOn(d, kind, key, geo) {
    if (kind === 'st') return stationOn(d, key, geo);
    if (kind === 'ds') return districtOn(d, key, geo);
    return has(d[LIST_OF[kind]] || [], key);
  }

  /**
   * Свёртка: линия, все станции которой выбраны (поимённо или другими целыми линиями),
   * становится одной меткой «Замоскворецкая линия»; округ, все районы которого
   * выбраны, — меткой «ЦАО целиком». Поимённые станции и районы под ней снимаются.
   */
  function normalize(d, geo) {
    if (!geo) return d;
    geo.lines.forEach(function(ln) {
      if (has(d.lines, ln.id) || !ln.st || ln.st.length < 2) return;
      var all = ln.st.every(function(slug) { return stationOn(d, slug, geo); });
      if (!all) return;
      d.lines.push(ln.id);
      ln.st.forEach(function(slug) { drop(d.stations, slug); });
    });
    Object.keys(geo.okrugDistricts).forEach(function(ok) {
      if (has(d.okrugs, ok)) return;
      var list = geo.okrugDistricts[ok];
      if (!list.length || !list.every(function(s) { return has(d.districts, s); })) return;
      d.okrugs.push(ok);
      list.forEach(function(s) { drop(d.districts, s); });
    });
    // Под целой линией и целым округом поимённых записей не остаётся.
    d.stations = d.stations.filter(function(slug) {
      return !stationLines(geo, slug).some(function(l) { return has(d.lines, l); });
    });
    d.districts = d.districts.filter(function(s) {
      var ds = geo.districtById[s];
      return !(ds && ds.o && has(d.okrugs, ds.o));
    });
    return d;
  }

  /**
   * Нажатие на строку. Возвращает НОВЫЙ черновик — старый не трогается.
   *   • станция из целой линии снята → линия перестаёт быть целой, её остальные
   *     станции переходят в выбор поимённо (правило спецификации А2.3);
   *   • выбран округ → районы этого округа из выбора снимаются;
   *   • район из целого округа снят → остальные районы округа поимённо.
   */
  function toggle(draft, kind, key, on, geo) {
    var d = cloneDraft(draft);
    key = String(key);
    if (kind === 'st') {
      if (on) {
        if (!stationOn(d, key, geo)) d.stations.push(key);
      } else {
        drop(d.stations, key);
        stationLines(geo, key).forEach(function(lid) {
          if (!has(d.lines, lid)) return;
          drop(d.lines, lid);
          lineStations(geo, lid).forEach(function(slug) {
            if (slug !== key && !stationOn(d, slug, geo)) d.stations.push(slug);
          });
        });
      }
    } else if (kind === 'ln') {
      if (on) add(d.lines, key); else drop(d.lines, key);
    } else if (kind === 'ok') {
      if (on) add(d.okrugs, key); else drop(d.okrugs, key);
    } else if (kind === 'ds') {
      if (on) {
        if (!districtOn(d, key, geo)) d.districts.push(key);
      } else {
        drop(d.districts, key);
        var ds = geo && geo.districtById[key];
        if (ds && ds.o && has(d.okrugs, ds.o)) {
          drop(d.okrugs, ds.o);
          (geo.okrugDistricts[ds.o] || []).forEach(function(s) {
            if (s !== key) add(d.districts, s);
          });
        }
      }
    } else if (kind === 'pl') {
      if (on) add(d.places, key); else drop(d.places, key);
    }
    return normalize(d, geo);
  }

  /** Все станции выбора: поимённые плюс станции целых линий (так пишется адрес). */
  function expandStations(d, geo) {
    var out = d.stations.slice();
    d.lines.forEach(function(lid) {
      lineStations(geo, lid).forEach(function(slug) { add(out, slug); });
    });
    return out;
  }

  /** Время до метро на языке окна: 'any' (любое) · 'walk' (пешком) · 'transit' (на транспорте). */
  function timeState(d) {
    if (d.mode === 'walk') return 'walk';
    return +d.min > 0 ? 'transit' : 'any';
  }

  // ══════════════ ОТБОР ══════════════

  function toSet(list) {
    var s = {};
    (list || []).forEach(function(v) { s[v] = 1; });
    return s;
  }

  /** Черновик → готовое к проверке условие (множества вместо списков). */
  function compile(d, geo) {
    d = cloneDraft(d);
    var st = expandStations(d, geo);
    return {
      st: toSet(st), ok: toSet(d.okrugs), ds: toSet(d.districts), pl: toSet(d.places),
      min: d.min, mode: d.mode,
      hasStations: st.length > 0,
      active: st.length + d.okrugs.length + d.districts.length + d.places.length > 0
    };
  }

  /** Станция дома проходит правило времени (зеркало SQL сервера — см. шапку файла). */
  function stationOk(s, where) {
    var min = +where.min || 0;
    if (min > 0 && !(+s.time > 0 && +s.time <= min)) return false;
    if (where.mode === 'walk' && s.walk !== true) return false;
    return true;
  }

  /** Дом проходит «где искать»: хоть что-то из выбранного (ИЛИ). Пусто — проходят все. */
  function geoPasses(item, where) {
    if (!where || !where.active) return true;
    if (item.okrug && where.ok[item.okrug]) return true;
    if (item.rayon && where.ds[item.rayon]) return true;
    if (item.place && where.pl[item.place]) return true;
    if (where.hasStations) {
      var arr2 = Array.isArray(item.metro_full) ? item.metro_full : [];
      for (var i = 0; i < arr2.length; i++) {
        var s = arr2[i];
        if (s && where.st[s.slug] && stationOk(s, where)) return true;
      }
    }
    return false;
  }

  /**
   * Единое правило отбора для выдачи, кнопки и чисел: остальные фильтры страницы
   * (opts.base — предикат вызывающего) И «где искать».
   */
  function passes(item, where, opts) {
    var base = opts && opts.base;
    if (base && !base(item)) return false;
    return geoPasses(item, where);
  }

  /**
   * Числа у строк окна и число на кнопке — одним проходом по ЖК.
   *   total    — ЖК, прошедшие остальные фильтры (кнопка «Показать N ЖК» при пустом выборе);
   *   selected — они же И «где искать» (кнопка при выборе);
   *   st/ln/ok/ds/pl — сколько ЖК у станции, линии, в округе, районе, месте.
   * Числа у строк считаются БЕЗ самого «где искать» (выбор их не меняет), но С
   * правилом времени до метро: «пешком до 10 минут» меняет и числа у станций.
   */
  function facetCounts(items, where, opts) {
    var geo = opts && opts.geo;
    var base = opts && opts.base;
    var w = where || compile(emptyDraft(), geo);
    var out = { total: 0, selected: 0, st: {}, ln: {}, ok: {}, ds: {}, pl: {} };
    (items || []).forEach(function(it) {
      if (!it || (base && !base(it))) return;
      out.total++;
      if (geoPasses(it, w)) out.selected++;
      var seenSt = {};
      var seenLn = {};
      (Array.isArray(it.metro_full) ? it.metro_full : []).forEach(function(s) {
        if (!s || !s.slug || seenSt[s.slug] || !stationOk(s, w)) return;
        seenSt[s.slug] = 1;
        out.st[s.slug] = (out.st[s.slug] || 0) + 1;
        stationLines(geo, s.slug).forEach(function(lid) {
          if (seenLn[lid]) return;
          seenLn[lid] = 1;
          out.ln[lid] = (out.ln[lid] || 0) + 1;
        });
      });
      if (it.okrug) out.ok[it.okrug] = (out.ok[it.okrug] || 0) + 1;
      if (it.rayon) out.ds[it.rayon] = (out.ds[it.rayon] || 0) + 1;
      if (it.place) out.pl[it.place] = (out.pl[it.place] || 0) + 1;
    });
    return out;
  }

  // ══════════════ АДРЕС ══════════════

  var SLUG_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;

  /**
   * Список слагов из параметра адреса: латиница, цифры, дефис; без повторов; не
   * больше cap. Мусор отбрасывается молча — адрес могли поправить руками.
   * (Якоря ^…$ без флага m: в JS `$` — только конец строки.)
   */
  function parseSlugs(raw, cap) {
    var out = [];
    String(raw || '').split(',').forEach(function(part) {
      var s = part.trim().toLowerCase();
      if (s && SLUG_RE.test(s) && out.indexOf(s) === -1 && out.length < (cap || 150)) out.push(s);
    });
    return out;
  }

  // ══════════════ ЛЕВАЯ КОЛОНКА ФИЛЬТРОВ (шаг В, 25.09.2026) ══════════════
  //
  // Зачем (человеческим языком). Колонка «как у Авито» называет у каждого варианта
  // число ЖК и считает кнопку «Показать N ЖК». Считает ОДНО правило — здесь; выдача
  // после «Показать» (novostrojki.js → itemMatchesFilters) зовёт его же (colPasses).
  //
  // Два уровня признаков (решение 6 главного чата):
  //   • уровень ЖК — считает браузер по уже загруженному списку: комнаты (по сводке
  //     flat_summary), срок сдачи, класс, тип жилья, застройщик, скидки, паркинг,
  //     проекты (выбранные ЖК по номеру);
  //   • уровень квартиры — цена, площадь, этаж, «не первый / не последний», отделка —
  //     считает СЕРВЕР (map_api.php?action=flat_count&facets=1) и отвечает НОМЕРАМИ ЖК,
  //     а не квартирами. Условия квартиры выполняются в ОДНОЙ квартире: «2 комн. до
  //     15 млн» — это «есть двушка дешевле 15 млн». Как только задано хоть одно условие
  //     квартиры, комнаты тоже уходят на сервер — в ту же квартиру.
  //   • разделы — через И, варианты внутри раздела — через ИЛИ.
  // Число у варианта — все условия, КРОМЕ своего раздела (выбранное не схлопывает соседей).
  // ⛔ Квартиры в браузер не приходят: из ответа сервера здесь только номера ЖК.

  var COL_ROOMS = ['0', '1', '2', '3', '4'];
  var COL_FINISH = ['none', 'white', 'full'];
  var COL_CLASSES = ['economy', 'comfort', 'business', 'premium'];
  var COL_KINDS = ['flat', 'apart'];
  // Скидка «есть» с того же порога, с которого карточка рисует плашку «Скидки до −N%».
  // SSOT — FlatHandler::DISCOUNT_MIN_PCT; совпадение сторожит
  // tests/Integration/novostrojki-filters-column.test.mjs.
  var COL_DISCOUNT_MIN_PCT = 3;
  // Разделы колонки в порядке показа. 'flat' — служебный: ответ сервера по квартирам.
  // 'jk' — «Проекты» (25.09.2026, слово владельца «добавить в фильтр проекты, как у нас
  // было»): выбранные ЖК по номеру, внутри раздела — ИЛИ, с остальными — И.
  var COL_SECTIONS = ['rooms', 'deadline', 'cls', 'kind', 'dev', 'discount', 'parking', 'jk', 'flat'];
  // Номер ЖК в адресе и в состоянии: только цифры, без ведущего нуля; не больше 50 штук.
  var COL_JK_RE = /^[1-9]\d{0,9}$/;
  var COL_JK_MAX = 50;
  // Класс: подпись лесенки карточки (building_class) или сырой класс → корзина фильтра.
  // «Премиум и выше» — премиум и делюкс вместе (в городе делюкс — единицы ЖК).
  var COL_CLASS_OF = {
    'эконом': 'economy', 'economy': 'economy', 'econom': 'economy', 'стандарт': 'economy', 'standard': 'economy', 'standart': 'economy',
    'комфорт': 'comfort', 'comfort': 'comfort', 'комфорт+': 'comfort', 'comfort+': 'comfort',
    'бизнес': 'business', 'business': 'business',
    'премиум': 'premium', 'premium': 'premium', 'элит': 'premium', 'elite': 'premium',
    'делюкс': 'premium', 'deluxe': 'premium', 'де-люкс': 'premium'
  };
  var COL_ROOM_LABEL = { '0': 'Студия', '1': '1 комн.', '2': '2 комн.', '3': '3 комн.', '4': '4+ комн.' };
  var COL_FINISH_LABEL = { none: 'Без отделки', white: 'Предчистовая', full: 'С отделкой' };
  var COL_CLASS_LABEL = { economy: 'Эконом', comfort: 'Комфорт', business: 'Бизнес', premium: 'Премиум и выше' };
  var COL_KIND_LABEL = { flat: 'Квартиры', apart: 'Апартаменты' };

  function colEmpty() {
    return {
      rooms: [], priceFrom: 0, priceTo: 0, areaFrom: 0, areaTo: 0, floorFrom: 0, floorTo: 0,
      notFirst: false, notLast: false, deadline: [], finish: [], cls: [], kind: [], dev: [],
      discount: false, parking: false, jk: []
    };
  }

  function uniqIn(list, allowed) {
    var out = [];
    (Array.isArray(list) ? list : []).forEach(function(v) {
      var s = String(v);
      if ((!allowed || allowed.indexOf(s) !== -1) && out.indexOf(s) === -1) out.push(s);
    });
    return out;
  }

  function posNum(v) { var n = +v; return isFinite(n) && n > 0 ? n : 0; }

  /** Копия состояния колонки с проверкой значений; служебное `flat` не копируется. */
  function colClone(c) {
    c = c || {};
    var o = colEmpty();
    o.rooms = uniqIn(c.rooms, COL_ROOMS).sort();
    o.priceFrom = posNum(c.priceFrom); o.priceTo = posNum(c.priceTo);
    o.areaFrom = posNum(c.areaFrom); o.areaTo = posNum(c.areaTo);
    o.floorFrom = Math.floor(posNum(c.floorFrom)); o.floorTo = Math.floor(posNum(c.floorTo));
    o.notFirst = !!c.notFirst; o.notLast = !!c.notLast;
    o.deadline = uniqIn(c.deadline).filter(function(k) { return k === 'done' || /^20\d\d$/.test(k); }).sort(function(a, b) {
      return a === 'done' ? -1 : b === 'done' ? 1 : a.localeCompare(b);
    });
    o.finish = uniqIn(c.finish, COL_FINISH).sort();
    o.cls = uniqIn(c.cls, COL_CLASSES);
    o.kind = uniqIn(c.kind, COL_KINDS);
    o.dev = uniqIn(c.dev).filter(function(s) { return SLUG_RE.test(s); });
    o.discount = !!c.discount; o.parking = !!c.parking;
    // Проекты: номера ЖК строками, по возрастанию — порядок выбора не меняет состояния.
    o.jk = uniqIn(c.jk).filter(function(s) { return COL_JK_RE.test(s); })
      .sort(function(a, b) { return +a - +b; }).slice(0, COL_JK_MAX);
    // Перевёрнутый диапазон снимается целиком — как у сервера (FlatCountQuery::normalize).
    if (o.priceFrom && o.priceTo && o.priceFrom > o.priceTo) { o.priceFrom = 0; o.priceTo = 0; }
    if (o.areaFrom && o.areaTo && o.areaFrom > o.areaTo) { o.areaFrom = 0; o.areaTo = 0; }
    if (o.floorFrom && o.floorTo && o.floorFrom > o.floorTo) { o.floorFrom = 0; o.floorTo = 0; }
    return o;
  }

  /** Есть ли условие, которое знает только сервер (цена, площадь, этаж, отделка). */
  function colServerActive(c) {
    return !!(c.priceFrom || c.priceTo || c.areaFrom || c.areaTo || c.floorFrom || c.floorTo
      || c.notFirst || c.notLast || (c.finish && c.finish.length));
  }

  /** Ключ вопроса к серверу: комнаты + условия квартиры + отделка (признаки ЖК в него не входят). */
  function colFlatKey(c) {
    c = colClone(c);
    return JSON.stringify([c.rooms, c.priceFrom, c.priceTo, c.areaFrom, c.areaTo, c.floorFrom, c.floorTo, c.notFirst, c.notLast, c.finish]);
  }

  /** Параметры запроса `flat_count&facets=1` (без города). Номера ЖК не шлём: ответ — на весь город. */
  function colServerQuery(c) {
    c = colClone(c);
    var q = [];
    var put = function(k, v) { q.push(k + '=' + encodeURIComponent(String(v))); };
    if (c.rooms.length) put('rooms', c.rooms.join(','));
    if (c.priceFrom) put('price_min', Math.round(c.priceFrom));
    if (c.priceTo) put('price_max', Math.round(c.priceTo));
    if (c.areaFrom) put('area_min', c.areaFrom);
    if (c.areaTo) put('area_max', c.areaTo);
    if (c.floorFrom) put('floor_min', c.floorFrom);
    if (c.floorTo) put('floor_max', c.floorTo);
    if (c.notFirst) put('not_first', 1);
    if (c.notLast) put('not_last', 1);
    if (c.finish.length) put('finish', c.finish.join(','));
    return q.join('&');
  }

  /** Ответ сервера → множества номеров ЖК. null — ответ не годится. */
  function colFlatFrom(resp) {
    if (!resp || resp.ok !== true || !Array.isArray(resp.ids)) return null;
    var byRooms = {};
    (Array.isArray(resp.by_rooms) ? resp.by_rooms : []).forEach(function(list, r) { byRooms[String(r)] = toSet(list); });
    var byFinish = {};
    var bf = resp.by_finish && typeof resp.by_finish === 'object' ? resp.by_finish : {};
    COL_FINISH.forEach(function(k) { byFinish[k] = toSet(bf[k]); });
    return {
      ids: toSet(resp.ids), byRooms: byRooms, byFinish: byFinish,
      noRooms: toSet(resp.ids_no_rooms), noFinish: toSet(resp.ids_no_finish),
      bounds: resp.bounds && typeof resp.bounds === 'object' ? resp.bounds : {}
    };
  }

  function roomKeyOf(r) { var n = parseInt(r, 10); if (!(n >= 0)) return ''; return n >= 4 ? '4' : String(n); }

  /** Корзины комнат, в которых у ЖК есть свободные квартиры (по сводке flat_summary). */
  function itemRoomKeys(it) {
    var out = [];
    (Array.isArray(it.flat_summary) ? it.flat_summary : []).forEach(function(f) {
      var k = f && +f.cnt > 0 ? roomKeyOf(f.rooms) : '';
      if (k && out.indexOf(k) === -1) out.push(k);
    });
    return out;
  }

  /**
   * Ключ варианта срока → значение правила блока 8.4: 'done' → «Сдан», год — как есть.
   * ⛔ СВОЕГО ПРАВИЛА СРОКА ЗДЕСЬ НЕТ (доводка перед прод, 25.09.2026, проверка Б P1-2).
   * Отбирает и считает ОДНО правило — BuildStatus.matchesDeadline, то же, что у SQL
   * (Deadline::sqlBuildingCondition), карты и прежней выпадашки «Год сдачи»; паритет
   * держит общий набор tests/Fixtures/deadline-filter-cases.json. Прежние isReady /
   * isStale / deadlineYear здесь давали дому ОДИН ключ и расходились с 8.4 в 3 вариантах
   * из 7: дом на 100 % без ввода попадал в «Уже сдан», «Сдан 2026» выпадал из «2026».
   */
  function deadlineWant(key) { return key === 'done' ? 'Сдан' : String(key); }

  /**
   * Варианты срока, в которые попадает дом: 'done' и годы. Ключей может быть
   * несколько («Сдан 2026» — это и «Уже сдан», и «2026»), ни одного — срок неизвестен.
   * Годы-кандидаты — четыре цифры 20xx из строки срока, судит каждый matchesDeadline.
   */
  function deadlineKeys(it) {
    var BS = window.BuildStatus;
    var out = [];
    if (!BS || typeof BS.matchesDeadline !== 'function') return out;
    if (BS.matchesDeadline('Сдан', it.status, it.deadline)) out.push('done');
    var re = /20\d{2}/g;
    var s = String(it.deadline || '');
    var m;
    while ((m = re.exec(s)) !== null) {
      if (out.indexOf(m[0]) === -1 && BS.matchesDeadline(m[0], it.status, it.deadline)) out.push(m[0]);
    }
    return out;
  }

  /** Проходит ли дом хотя бы один выбранный вариант срока (внутри раздела — ИЛИ). */
  function deadlinePasses(it, keys) {
    var BS = window.BuildStatus;
    if (!keys.length) return true;
    if (!BS || typeof BS.matchesDeadline !== 'function') return false;
    for (var i = 0; i < keys.length; i++) {
      if (BS.matchesDeadline(deadlineWant(keys[i]), it.status, it.deadline)) return true;
    }
    return false;
  }

  /**
   * Класс ЖК → корзина. Подпись лесенки карточки бывает составной: «Клубный дом ·
   * Делюкс» (ЖК 58, класс куратора deluxe при пустом сыром). Раньше её не узнавали,
   * и ЖК выпадал из «Премиум и выше» (проверка А P2-1: 19 против 20 в SQL). Теперь
   * подпись читается целиком, затем по частям через «·»; последним — сырой класс.
   */
  function classKey(it) {
    var lbl = String(it.building_class || '').trim().toLowerCase();
    if (COL_CLASS_OF[lbl]) return COL_CLASS_OF[lbl];
    var parts = lbl.split(/\s*[·•|]\s*/);
    for (var i = parts.length - 1; i >= 0; i--) {
      if (COL_CLASS_OF[parts[i]]) return COL_CLASS_OF[parts[i]];
    }
    var raw = String(it['class'] || '').trim().toLowerCase();
    return COL_CLASS_OF[raw] || '';
  }

  function kindKey(it) { return parseInt(it.is_apartments, 10) === 1 ? 'apart' : 'flat'; }
  function hasDiscount(it) { return (parseInt(it.discount_max, 10) || 0) >= COL_DISCOUNT_MIN_PCT; }
  function hasParking(it) { return String(it.parking_type || '').trim() !== ''; }

  function inSet(set, id) { return !!(set && set[id]); }

  /** Проходит ли ЖК раздел колонки (неактивный раздел — проходит). flat — ответ сервера. */
  function colSecPass(sec, it, c, flat) {
    switch (sec) {
      case 'rooms':
        if (!c.rooms.length || colServerActive(c)) return true;  // с условиями квартиры — сервер
        var keys = itemRoomKeys(it);
        for (var i = 0; i < c.rooms.length; i++) { if (keys.indexOf(c.rooms[i]) !== -1) return true; }
        return false;
      case 'deadline': return deadlinePasses(it, c.deadline);
      case 'cls': return !c.cls.length || c.cls.indexOf(classKey(it)) !== -1;
      case 'kind': return c.kind.length !== 1 || kindKey(it) === c.kind[0];
      case 'dev': return !c.dev.length || c.dev.indexOf(String(it.developer_slug || '')) !== -1;
      case 'discount': return !c.discount || hasDiscount(it);
      case 'parking': return !c.parking || hasParking(it);
      case 'jk': return !c.jk.length || c.jk.indexOf(String(it.id)) !== -1;
      case 'flat':
        // Ответ ещё не пришёл — условие квартиры не судим (выдача перерисуется по ответу).
        if (!colServerActive(c) || !flat) return true;
        return inSet(flat.ids, it.id);
    }
    return true;
  }

  /**
   * ЖК проходит колонку. c — применённое состояние; c.flat — ответ сервера для него
   * (colFlatFrom) или null. Это правило зовёт выдача (itemMatchesFilters).
   */
  function colPasses(it, c) {
    if (!c) return true;
    var flat = c.flat || null;
    var cc = colClone(c);
    for (var i = 0; i < COL_SECTIONS.length; i++) {
      if (!colSecPass(COL_SECTIONS[i], it, cc, flat)) return false;
    }
    return true;
  }

  /**
   * Числа колонки одним проходом.
   *   opts.base — остальные фильтры страницы (где искать, поиск, проект);
   *   opts.flat — ответ сервера для ТЕКУЩЕГО ключа (colFlatKey) или null (ждём).
   * Ответ: total (кнопка), base (без колонки), числа вариантов по разделам,
   * pending — true, если без ответа сервера числа неполные.
   */
  function colCounts(items, c, opts) {
    c = colClone(c);
    var flat = (opts && opts.flat) || null;
    var base = opts && opts.base;
    var srv = colServerActive(c);
    var out = { total: 0, base: 0, rooms: {}, deadline: {}, cls: {}, kind: { flat: 0, apart: 0 }, dev: {},
      discount: 0, parking: 0, jk: {}, finish: flat ? {} : null, pending: !flat };
    COL_ROOMS.forEach(function(k) { out.rooms[k] = 0; });
    COL_CLASSES.forEach(function(k) { out.cls[k] = 0; });
    if (flat) COL_FINISH.forEach(function(k) { out.finish[k] = 0; });
    (items || []).forEach(function(it) {
      if (!it || (base && !base(it))) return;
      out.base++;
      var p = {};
      var fails = 0;
      COL_SECTIONS.forEach(function(s) {
        p[s] = colSecPass(s, it, c, flat);
        if (!p[s]) fails++;
      });
      if (!fails) out.total++;
      // Раздел s считается «без себя»: проходят все прочие разделы.
      var others = function(skip) {
        if (!fails) return true;
        for (var i = 0; i < COL_SECTIONS.length; i++) {
          var s = COL_SECTIONS[i];
          if (!p[s] && skip.indexOf(s) === -1) return false;
        }
        return true;
      };
      if (srv) {
        if (flat && others(['rooms', 'flat'])) {
          COL_ROOMS.forEach(function(k) { if (inSet(flat.byRooms[k], it.id)) out.rooms[k]++; });
        }
      } else if (others(['rooms'])) {
        itemRoomKeys(it).forEach(function(k) { out.rooms[k]++; });
      }
      if (flat && others(['rooms', 'flat'])) {
        COL_FINISH.forEach(function(k) { if (inSet(flat.byFinish[k], it.id)) out.finish[k]++; });
      }
      if (others(['deadline'])) {
        deadlineKeys(it).forEach(function(dk) { out.deadline[dk] = (out.deadline[dk] || 0) + 1; });
      }
      if (others(['cls'])) { var ck = classKey(it); if (ck) out.cls[ck]++; }
      if (others(['kind'])) out.kind[kindKey(it)]++;
      if (others(['dev'])) { var dv = String(it.developer_slug || ''); if (dv) out.dev[dv] = (out.dev[dv] || 0) + 1; }
      if (others(['discount']) && hasDiscount(it)) out.discount++;
      if (others(['parking']) && hasParking(it)) out.parking++;
      // Проект: 1 — ЖК подходит под все прочие условия (0 — вариант бледный).
      if (others(['jk'])) out.jk[String(it.id)] = 1;
    });
    return out;
  }

  /** Сколько ЖК пройдут колонку (без чисел вариантов) — для подсказки при нуле. */
  function colTotal(items, c, flat, base) {
    var cc = colClone(c);
    var n = 0;
    (items || []).forEach(function(it) {
      if (!it || (base && !base(it))) return;
      for (var i = 0; i < COL_SECTIONS.length; i++) { if (!colSecPass(COL_SECTIONS[i], it, cc, flat)) return; }
      n++;
    });
    return n;
  }

  /** Разделы, которые сейчас что-то ограничивают (порядок — как в колонке). */
  function colActiveSections(c) {
    c = colClone(c);
    var out = [];
    if (c.rooms.length) out.push('rooms');
    if (c.priceFrom || c.priceTo) out.push('price');
    if (c.areaFrom || c.areaTo) out.push('area');
    if (c.floorFrom || c.floorTo || c.notFirst || c.notLast) out.push('floor');
    if (c.deadline.length) out.push('deadline');
    if (c.finish.length) out.push('finish');
    if (c.cls.length) out.push('cls');
    if (c.kind.length === 1) out.push('kind');
    if (c.dev.length) out.push('dev');
    if (c.jk.length) out.push('jk');
    if (c.discount) out.push('discount');
    if (c.parking) out.push('parking');
    return out;
  }

  /** Состояние без раздела sec («ослабить условие»). */
  function colWithout(c, sec) {
    var o = colClone(c);
    switch (sec) {
      case 'rooms': o.rooms = []; break;
      case 'price': o.priceFrom = 0; o.priceTo = 0; break;
      case 'area': o.areaFrom = 0; o.areaTo = 0; break;
      case 'floor': o.floorFrom = 0; o.floorTo = 0; o.notFirst = false; o.notLast = false; break;
      case 'deadline': o.deadline = []; break;
      case 'finish': o.finish = []; break;
      case 'cls': o.cls = []; break;
      case 'kind': o.kind = []; break;
      case 'dev': o.dev = []; break;
      case 'jk': o.jk = []; break;
      case 'discount': o.discount = false; break;
      case 'parking': o.parking = false; break;
    }
    return o;
  }

  /**
   * Подсказка при нуле (спецификация В.7): сколько ЖК нашлось бы без каждого раздела.
   * opts.flat — ответ сервера для c; opts.flatFor(c2) — ответ для ослабленного
   * состояния (из кэша или null — тогда раздел пропускается, его спросят отдельно).
   * Ответ — [{ sec, n }] по убыванию n, только n > 0.
   */
  function colRelax(items, c, opts) {
    c = colClone(c);
    var base = opts && opts.base;
    var flat = (opts && opts.flat) || null;
    var flatFor = (opts && opts.flatFor) || function() { return null; };
    var out = [];
    colActiveSections(c).forEach(function(sec) {
      var c2 = colWithout(c, sec);
      var f2 = null;
      if (colServerActive(c2)) {
        if (sec === 'rooms' && flat) f2 = { ids: flat.noRooms };
        else if (sec === 'finish' && flat) f2 = { ids: flat.noFinish };
        else f2 = flatFor(c2);
        if (!f2) return;
      }
      var n = colTotal(items, c2, f2, base);
      if (n > 0) out.push({ sec: sec, n: n });
    });
    out.sort(function(a, b) { return b.n - a.n; });
    return out;
  }

  // ─── Подписи и адрес ───

  function mln(v) {
    var m = Math.round(v / 1e5) / 10;
    return String(m).replace('.', ',');
  }
  function numRu(v) { return String(Math.round(v * 10) / 10).replace('.', ','); }
  function rangeLabel(from, to, fmt, unit, prefix) {
    if (from && to) return (prefix || '') + fmt(from) + '–' + fmt(to) + unit;
    if (from) return (prefix || '') + 'от ' + fmt(from) + unit;
    return (prefix || '') + 'до ' + fmt(to) + unit;
  }

  /**
   * Чипсы применённого: [{ sec, key, label }]. names.dev — слаг → имя бренда,
   * names.jk — номер ЖК → название. Крестик на чипсе снимает ровно это условие (colRemove).
   */
  function colChips(c, names) {
    c = colClone(c);
    var dev = (names && names.dev) || {};
    var jk = (names && names.jk) || {};
    var out = [];
    c.rooms.forEach(function(k) { out.push({ sec: 'rooms', key: k, label: COL_ROOM_LABEL[k] }); });
    if (c.priceFrom || c.priceTo) out.push({ sec: 'price', key: '', label: rangeLabel(c.priceFrom, c.priceTo, mln, ' млн ₽') });
    if (c.areaFrom || c.areaTo) out.push({ sec: 'area', key: '', label: rangeLabel(c.areaFrom, c.areaTo, numRu, ' м²') });
    if (c.floorFrom || c.floorTo) {
      out.push({ sec: 'floorRange', key: '', label: c.floorFrom && c.floorTo ? 'этаж ' + c.floorFrom + '–' + c.floorTo
        : (c.floorFrom ? 'этаж от ' + c.floorFrom : 'этаж до ' + c.floorTo) });
    }
    if (c.notFirst) out.push({ sec: 'notFirst', key: '', label: 'Не первый этаж' });
    if (c.notLast) out.push({ sec: 'notLast', key: '', label: 'Не последний этаж' });
    c.deadline.forEach(function(k) { out.push({ sec: 'deadline', key: k, label: k === 'done' ? 'Уже сдан' : 'Сдача ' + k }); });
    c.finish.forEach(function(k) { out.push({ sec: 'finish', key: k, label: COL_FINISH_LABEL[k] }); });
    c.cls.forEach(function(k) { out.push({ sec: 'cls', key: k, label: COL_CLASS_LABEL[k] }); });
    if (c.kind.length === 1) out.push({ sec: 'kind', key: c.kind[0], label: COL_KIND_LABEL[c.kind[0]] });
    c.dev.forEach(function(k) { out.push({ sec: 'dev', key: k, label: dev[k] || k }); });
    // Проекты — по алфавиту названий (в состоянии они по номеру).
    c.jk.map(function(k) { return { sec: 'jk', key: k, label: jk[k] || 'ЖК' }; })
      .sort(function(a, b) { return a.label.localeCompare(b.label, 'ru'); })
      .forEach(function(x) { out.push(x); });
    if (c.discount) out.push({ sec: 'discount', key: '', label: 'Есть скидки' });
    if (c.parking) out.push({ sec: 'parking', key: '', label: 'Есть паркинг' });
    return out;
  }

  /** Снять одно условие (крестик чипса). Возвращает НОВОЕ состояние. */
  function colRemove(c, sec, key) {
    var o = colClone(c);
    var drop2 = function(list) { return list.filter(function(v) { return v !== String(key); }); };
    switch (sec) {
      case 'rooms': case 'deadline': case 'finish': case 'cls': case 'dev': case 'jk': o[sec] = drop2(o[sec]); break;
      case 'kind': o.kind = []; break;
      case 'price': o.priceFrom = 0; o.priceTo = 0; break;
      case 'area': o.areaFrom = 0; o.areaTo = 0; break;
      case 'floorRange': o.floorFrom = 0; o.floorTo = 0; break;
      case 'floor': o.floorFrom = 0; o.floorTo = 0; o.notFirst = false; o.notLast = false; break;
      case 'notFirst': o.notFirst = false; break;
      case 'notLast': o.notLast = false; break;
      case 'discount': o.discount = false; break;
      case 'parking': o.parking = false; break;
    }
    return o;
  }

  /** Число ограничивающих разделов — для «Фильтры · N». */
  function colActiveCount(c) { return colActiveSections(c).length; }

  // Параметры адреса колонки (спецификация В.5). Цена — в млн ₽, как у прежнего фильтра.
  // project=12,34 — «Проекты» (номера ЖК; имя — по прежней выпадашке «Проект»).
  var COL_PARAMS = ['rooms', 'price_from', 'price_to', 'area_from', 'area_to', 'floor_from', 'floor_to',
    'not_first', 'not_last', 'deadline', 'finish', 'class', 'type', 'developer', 'project', 'discount', 'parking'];

  function numParam(raw, max) {
    var s = String(raw == null ? '' : raw).trim().replace(',', '.');
    if (!/^\d{1,6}(\.\d{1,2})?$/.test(s)) return 0;
    var n = parseFloat(s);
    return n > 0 && n <= max ? n : 0;
  }

  /**
   * Состояние колонки из адреса. get(name) → строка или null. Понимает и прежний вид
   * одиночных значений, который шлёт фильтр главной: rooms=Студия / 5, deadline=Сдан.
   */
  function colFromParams(get) {
    var o = colEmpty();
    var list = function(name) { var v = get(name); return v == null ? [] : String(v).split(',').map(function(s) { return s.trim(); }).filter(Boolean); };
    o.rooms = list('rooms').map(function(r) {
      if (r === 'Студия' || r.toLowerCase() === 'studio') return '0';
      // 0–4 и прежнее «5» (5+ комн. фильтра главной) — в корзину «4 и больше».
      return /^[0-5]$/.test(r) ? roomKeyOf(r) : '';
    }).filter(Boolean);
    o.priceFrom = Math.round(numParam(get('price_from'), 1000) * 1e6);
    o.priceTo = Math.round(numParam(get('price_to'), 1000) * 1e6);
    o.areaFrom = numParam(get('area_from'), 10000);
    o.areaTo = numParam(get('area_to'), 10000);
    o.floorFrom = Math.floor(numParam(get('floor_from'), 200));
    o.floorTo = Math.floor(numParam(get('floor_to'), 200));
    o.notFirst = get('not_first') === '1';
    o.notLast = get('not_last') === '1';
    o.deadline = list('deadline').map(function(d) { return /^сдан$/i.test(d) ? 'done' : d; });
    o.finish = list('finish');
    o.cls = list('class').map(function(k) { return COL_CLASS_OF[k.toLowerCase()] || k; });
    var t = get('type');
    o.kind = t === 'flat' ? ['flat'] : (t === 'apart' || t === 'apartments') ? ['apart'] : [];
    o.dev = list('developer').map(function(s) { return s.toLowerCase(); });
    // Мусор (не номер) отбрасывает colClone; номер, которого нет в городе, — колонка
    // по приходу списка ЖК (иначе выдача была бы пустой по чужой ссылке).
    o.jk = list('project');
    o.discount = get('discount') === '1';
    o.parking = get('parking') === '1';
    return colClone(o);
  }

  /** Состояние колонки → [[имя, значение]] для адреса (пустое не пишется). */
  function colToParams(c) {
    c = colClone(c);
    var out = [];
    var numOut = function(v) { return String(Math.round(v * 100) / 100); };
    if (c.rooms.length) out.push(['rooms', c.rooms.join(',')]);
    if (c.priceFrom) out.push(['price_from', numOut(c.priceFrom / 1e6)]);
    if (c.priceTo) out.push(['price_to', numOut(c.priceTo / 1e6)]);
    if (c.areaFrom) out.push(['area_from', numOut(c.areaFrom)]);
    if (c.areaTo) out.push(['area_to', numOut(c.areaTo)]);
    if (c.floorFrom) out.push(['floor_from', String(c.floorFrom)]);
    if (c.floorTo) out.push(['floor_to', String(c.floorTo)]);
    if (c.notFirst) out.push(['not_first', '1']);
    if (c.notLast) out.push(['not_last', '1']);
    if (c.deadline.length) out.push(['deadline', c.deadline.join(',')]);
    if (c.finish.length) out.push(['finish', c.finish.join(',')]);
    if (c.cls.length) out.push(['class', c.cls.join(',')]);
    if (c.kind.length === 1) out.push(['type', c.kind[0]]);
    if (c.dev.length) out.push(['developer', c.dev.join(',')]);
    if (c.jk.length) out.push(['project', c.jk.join(',')]);
    if (c.discount) out.push(['discount', '1']);
    if (c.parking) out.push(['parking', '1']);
    return out;
  }

  /** Одинаковы ли два состояния (для «Условия изменены»). */
  function colSame(a, b) { return JSON.stringify(colClone(a)) === JSON.stringify(colClone(b)); }

  window.CatalogFacets = {
    KINDS: KINDS,
    norm: norm,
    normQuery: normQuery,
    matchAt: matchAt,
    search: search,
    prepareGeo: prepareGeo,
    stationLines: stationLines,
    lineStations: lineStations,
    buildIndex: buildIndex,
    emptyDraft: emptyDraft,
    cloneDraft: cloneDraft,
    draftSize: draftSize,
    isOn: isOn,
    normalize: normalize,
    toggle: toggle,
    expandStations: expandStations,
    timeState: timeState,
    compile: compile,
    stationOk: stationOk,
    geoPasses: geoPasses,
    passes: passes,
    facetCounts: facetCounts,
    parseSlugs: parseSlugs,
    // Шаг В — левая колонка фильтров.
    COL_ROOMS: COL_ROOMS,
    COL_FINISH: COL_FINISH,
    COL_CLASSES: COL_CLASSES,
    COL_PARAMS: COL_PARAMS,
    COL_JK_MAX: COL_JK_MAX,
    COL_DISCOUNT_MIN_PCT: COL_DISCOUNT_MIN_PCT,
    COL_ROOM_LABEL: COL_ROOM_LABEL,
    COL_FINISH_LABEL: COL_FINISH_LABEL,
    COL_CLASS_LABEL: COL_CLASS_LABEL,
    COL_KIND_LABEL: COL_KIND_LABEL,
    colEmpty: colEmpty,
    colClone: colClone,
    colServerActive: colServerActive,
    colFlatKey: colFlatKey,
    colServerQuery: colServerQuery,
    colFlatFrom: colFlatFrom,
    itemRoomKeys: itemRoomKeys,
    deadlineKeys: deadlineKeys,
    // Прежнее имя (пробы проверок печатают ключи дома) — теперь список ключей.
    deadlineKey: deadlineKeys,
    deadlinePasses: deadlinePasses,
    classKey: classKey,
    kindKey: kindKey,
    hasDiscount: hasDiscount,
    hasParking: hasParking,
    colPasses: colPasses,
    colCounts: colCounts,
    colTotal: colTotal,
    colActiveSections: colActiveSections,
    colWithout: colWithout,
    colRelax: colRelax,
    colChips: colChips,
    colRemove: colRemove,
    colActiveCount: colActiveCount,
    colFromParams: colFromParams,
    colToParams: colToParams,
    colSame: colSame
  };
})();
